import { useRef, useState, type ReactNode } from 'react';
import { Badge, Icon, Popover } from '../ui';
import { SEO_MOVE_TARGETS, SEO_STAGES, seoStatus, type SeoStatus } from '../../lib/seoOpportunities';

/**
 * "Move to" for Ideation rows: every status a person can set by hand, grouped under the tab it
 * lands in. `children` is the trigger — a badge on the panel and the list, a button for a
 * selection — and a click on it never reaches the table row underneath.
 */

interface Props {
  /** The row's status, marked and inert in the menu. Omit for a mixed selection. */
  current?: SeoStatus;
  /** Some row being moved has an agent job out. Moving it does not cancel the job. */
  jobOut?: boolean;
  disabled?: boolean;
  align?: 'left' | 'right';
  onMove: (status: SeoStatus) => void;
  children: ReactNode;
}

export default function StatusMenu({ current, jobOut, disabled, align = 'left', onMove, children }: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <span
      ref={anchorRef}
      style={{ display: 'inline-flex' }}
      onClick={(e) => {
        e.stopPropagation();
        // The menu is portaled but React still bubbles its clicks here; only the trigger toggles.
        if (!disabled && anchorRef.current?.contains(e.target as Node)) setOpen((o) => !o);
      }}
    >
      {children}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        align={align}
        minWidth={300}
        role="menu"
        style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: '340px', overflowY: 'auto' }}
      >
        {jobOut ? (
          <p
            style={{
              margin: '4px 6px 8px',
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--status-warn-soft)',
              fontSize: 'var(--text-xs)',
              lineHeight: 1.45,
              color: 'var(--text-body)',
            }}
          >
            An agent job is out on this. Moving it does not cancel the job.
          </p>
        ) : null}
        {SEO_STAGES.map((stage) => {
          const targets = stage.statuses.filter((s) => SEO_MOVE_TARGETS[s]);
          if (!targets.length) return null;
          return (
            <div key={stage.id} style={{ display: 'grid', gap: '1px' }}>
              <div className="t-eyebrow" style={{ fontSize: 'var(--text-2xs)', padding: '8px 10px 4px' }}>
                {stage.label}
              </div>
              {targets.map((s) => {
                const here = s === current;
                const st = seoStatus(s);
                return (
                  <button
                    key={s}
                    role="menuitemradio"
                    aria-checked={here}
                    disabled={here}
                    className={here ? undefined : 'hover-wash pressable'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      onMove(s);
                    }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 14px',
                      gap: '4px 10px',
                      alignItems: 'center',
                      padding: '7px 10px',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      background: here ? 'var(--surface-raised)' : 'transparent',
                      cursor: here ? 'default' : 'pointer',
                      textAlign: 'left',
                      fontFamily: 'var(--font-ui)',
                    }}
                  >
                    <span>
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </span>
                    <span style={{ color: 'var(--accent-text)', display: 'inline-flex' }}>{here ? <Icon name="check" size={14} /> : null}</span>
                    <span style={{ gridColumn: '1 / -1', fontSize: 'var(--text-xs)', lineHeight: 1.4, color: 'var(--text-muted)' }}>
                      {SEO_MOVE_TARGETS[s]}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </Popover>
    </span>
  );
}

/** The status badge as a trigger: the badge a person already reads, plus a chevron that says it opens. */
export function StatusTrigger({ status, disabled }: { status: SeoStatus; disabled?: boolean }) {
  const st = seoStatus(status);
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-label={`Status: ${st.label}. Move to another status`}
      title="Move to another status"
      disabled={disabled}
      className="pressable"
      style={{ display: 'inline-flex', padding: 0, border: 'none', background: 'transparent', cursor: disabled ? 'default' : 'pointer' }}
    >
      <Badge tone={st.tone}>
        {st.label}
        <Icon name="chevron-down" size={11} strokeWidth={2.25} />
      </Badge>
    </button>
  );
}
